import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Alert from 'react-bootstrap/Alert';
import Form from 'react-bootstrap/Form';
import Dropdown from 'react-bootstrap/Dropdown';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import AdminLayout from '../templates/AdminLayout';
import SearchBar from '../molecules/SearchBar';
import MenuList from '../organisms/MenuList';
import AppButton from '../atoms/AppButton';
import AppBadge from '../atoms/AppBadge';
import AppSpinner from '../atoms/AppSpinner';
import { IconBurger, IconGrip, IconPencil, IconTag, IconTrash, IconMoreVertical, IconPlus, IconChevronDown, IconPlusCircle } from '../atoms/Icon';
import CategoryFormModal from '../organisms/CategoryFormModal';
import ProductFormModal from '../organisms/ProductFormModal';
import useConfirmDialog from '../organisms/ConfirmDialog';
import { useToast } from '../contexts/ToastContext';
import { titleCase } from '../utils/text';
import {
  listCategories, listProducts,
  deleteCategory, deleteProduct,
  updateCategory, updateProduct,
} from '../api/menuApi';

const AVAILABILITY_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'available', label: 'Disponíveis' },
  { value: 'unavailable', label: 'Indisponíveis' },
];

const byOrder = (a, b) => a.sortOrder - b.sortOrder;

function reorder(list, from, to) {
  const copy = [...list];
  const [moved] = copy.splice(from, 1);
  copy.splice(to, 0, moved);
  return copy;
}

function MenuPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');

  const [categoryModal, setCategoryModal] = useState(null);
  const [productModal, setProductModal] = useState(null);
  const { confirm, alert: alertDialog, dialog } = useConfirmDialog();
  const toast = useToast();

  const load = useCallback(async () => {
    try {
      const [cats, prods] = await Promise.all([listCategories(), listProducts()]);
      setCategories([...cats].sort(byOrder));
      setProducts(prods);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    return categories
      .filter((category) => !categoryFilter || category.id === categoryFilter)
      .map((category) => ({
        category,
        products: products
          .filter((p) => p.categoryId === category.id)
          .filter((p) => !q || p.name.toLowerCase().includes(q))
          .filter((p) => (
            availabilityFilter === 'all'
              || (availabilityFilter === 'available' ? p.isAvailable : !p.isAvailable)
          ))
          .sort(byOrder),
      }));
  }, [categories, products, search, categoryFilter, availabilityFilter]);

  const totalResults = useMemo(
    () => grouped.reduce((sum, g) => sum + g.products.length, 0),
    [grouped],
  );

  // Itens novos entram no fim da lista onde vão aparecer — é onde se espera
  // encontrá-los depois de criar.
  const nextCategorySortOrder = useCallback(
    () => categories.reduce((max, c) => Math.max(max, c.sortOrder + 1), 0),
    [categories],
  );

  const nextProductSortOrder = useCallback(
    (categoryId) => products
      .filter((p) => p.categoryId === categoryId)
      .reduce((max, p) => Math.max(max, p.sortOrder + 1), 0),
    [products],
  );

  // Durante busca ou filtro a lista exibida é um recorte, então os índices do
  // arrasto não corresponderiam à ordem real — mesmo motivo pelo qual o Kanban
  // desabilita o arrasto ao filtrar.
  const isFiltering = !!search.trim() || !!categoryFilter || availabilityFilter !== 'all';

  function clearFilters() {
    setSearch('');
    setCategoryFilter('');
    setAvailabilityFilter('all');
  }

  async function persistOrder(requests) {
    try {
      await Promise.all(requests);
    } catch (e) {
      toast.error(`Não foi possível salvar a nova ordem: ${e.message}`);
      load(); // desfaz o otimismo voltando ao que está no servidor
    }
  }

  const productsOf = useCallback(
    (categoryId) => products.filter((p) => p.categoryId === categoryId).sort(byOrder),
    [products],
  );

  // Mesma renumeração do arrasto, só que disparada por um clique — o toque em
  // telas pequenas não é confiável pra arrastar (por isso a alça some no
  // celular), e sem isto não haveria nenhuma forma de reordenar por lá.
  function moveProduct(product, direction) {
    const list = productsOf(product.categoryId);
    const from = list.findIndex((p) => p.id === product.id);
    const to = from + direction;
    if (from === -1 || to < 0 || to >= list.length) return;

    const touched = reorder(list, from, to).map((p, index) => ({ ...p, sortOrder: index }));
    const before = new Map(list.map((p) => [p.id, p.sortOrder]));
    const patched = new Map(touched.map((p) => [p.id, p]));
    setProducts((prev) => prev.map((p) => patched.get(p.id) || p));
    persistOrder(
      touched
        .filter((p) => before.get(p.id) !== p.sortOrder)
        .map((p) => updateProduct(p.id, { sortOrder: p.sortOrder })),
    );
  }

  // Mesma ideia do moveProduct, para a alça de categoria — some no celular
  // por causa do alvo de toque gigante (44px) que ela ganha nesse tamanho.
  function moveCategory(category, direction) {
    const list = [...categories].sort(byOrder);
    const from = list.findIndex((c) => c.id === category.id);
    const to = from + direction;
    if (from === -1 || to < 0 || to >= list.length) return;

    const touched = reorder(list, from, to).map((c, index) => ({ ...c, sortOrder: index }));
    const before = new Map(list.map((c) => [c.id, c.sortOrder]));
    setCategories(touched);
    persistOrder(
      touched
        .filter((c) => before.get(c.id) !== c.sortOrder)
        .map((c) => updateCategory(c.id, { sortOrder: c.sortOrder })),
    );
  }

  function handleDragEnd({ source, destination, type }) {
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    if (type === 'category') {
      const next = reorder(categories, source.index, destination.index)
        .map((c, index) => ({ ...c, sortOrder: index }));
      const before = new Map(categories.map((c) => [c.id, c.sortOrder]));
      setCategories(next);
      persistOrder(
        next
          .filter((c) => before.get(c.id) !== c.sortOrder)
          .map((c) => updateCategory(c.id, { sortOrder: c.sortOrder })),
      );
      return;
    }

    const fromCategory = source.droppableId.replace('products-', '');
    const toCategory = destination.droppableId.replace('products-', '');
    const listOf = productsOf;

    let touched;
    if (fromCategory === toCategory) {
      touched = reorder(listOf(fromCategory), source.index, destination.index)
        .map((p, index) => ({ ...p, sortOrder: index }));
    } else {
      // Soltar na lista de outra categoria move o produto para ela: as duas
      // pontas precisam ser renumeradas.
      const origin = listOf(fromCategory);
      const target = listOf(toCategory);
      const [moved] = origin.splice(source.index, 1);
      target.splice(destination.index, 0, { ...moved, categoryId: toCategory });
      touched = [
        ...origin.map((p, index) => ({ ...p, sortOrder: index })),
        ...target.map((p, index) => ({ ...p, sortOrder: index })),
      ];
    }

    const before = new Map(products.map((p) => [p.id, p]));
    const changed = touched.filter((p) => {
      const old = before.get(p.id);
      return old.sortOrder !== p.sortOrder || old.categoryId !== p.categoryId;
    });

    const patched = new Map(touched.map((p) => [p.id, p]));
    setProducts(products.map((p) => patched.get(p.id) || p));
    persistOrder(
      changed.map((p) => updateProduct(p.id, { sortOrder: p.sortOrder, categoryId: p.categoryId })),
    );
  }

  async function handleDeleteCategory(category) {
    const hasProducts = products.some((p) => p.categoryId === category.id);
    if (hasProducts) {
      await alertDialog('Esta categoria tem produtos e não pode ser excluída. Mova ou remova os produtos dela antes de excluí-la — excluir uma categoria nunca leva os produtos junto.', {
        title: 'Ação bloqueada',
      });
      return;
    }
    const ok = await confirm(`Excluir a categoria "${category.name}"? Ela não tem produtos, então nada mais será afetado.`, {
      title: 'Excluir categoria',
      confirmLabel: 'Excluir',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteCategory(category.id);
      load();
      toast.success('Categoria excluída com sucesso!');
    } catch (e) {
      await alertDialog(e.message, { title: 'Erro', danger: true });
    }
  }

  async function handleDeleteProduct(product) {
    const ok = await confirm(`Excluir o produto "${product.name}"?`, {
      title: 'Excluir produto',
      confirmLabel: 'Excluir',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteProduct(product.id);
      load();
      toast.success('Produto excluído com sucesso!');
    } catch (e) {
      await alertDialog(e.message, { title: 'Erro', danger: true });
    }
  }

  // Otimista, sem recarregar a lista: o toque precisa refletir na hora, e um
  // `load()` reordenaria/recarregaria tudo por uma mudança de um campo só.
  async function handleToggleAvailability(product) {
    const nextValue = !product.isAvailable;
    setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, isAvailable: nextValue } : p)));
    try {
      await updateProduct(product.id, { isAvailable: nextValue });
      toast.success(nextValue ? 'Produto disponível no cardápio.' : 'Produto marcado como indisponível.', {
        action: {
          label: 'Desfazer',
          onClick: () => handleToggleAvailability({ ...product, isAvailable: nextValue }),
        },
      });
    } catch (e) {
      setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, isAvailable: product.isAvailable } : p)));
      toast.error(`Não foi possível atualizar a disponibilidade: ${e.message}`);
    }
  }

  async function handleNewProduct(categoryId) {
    if (categories.length === 0) {
      await alertDialog('Crie uma categoria antes de cadastrar produtos.', { title: 'Ação bloqueada' });
      return;
    }
    setProductModal({ isNew: true, categoryId });
  }

  if (loading) {
    return (
      <AdminLayout>
        <AppSpinner />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="d-flex justify-content-between align-items-center gap-2 mb-4 flex-wrap">
        <h4 className="fw-semibold mb-0" style={{ color: 'var(--color-neutral)' }}>Cardápio</h4>
        <div className="d-flex gap-2">
          <AppButton variant="ghost" onClick={() => navigate('/cardapio/adicionais')} className="d-flex align-items-center gap-1">
            <IconPlusCircle size={15} /> Grupos de adicionais
          </AppButton>
          <AppButton variant="ghost" onClick={() => setCategoryModal('new')} className="d-flex align-items-center gap-1">
            <IconTag size={15} /> Nova categoria
          </AppButton>
          <AppButton variant="primary" onClick={() => handleNewProduct(undefined)} className="d-flex align-items-center gap-1">
            <IconBurger size={15} /> Novo produto
          </AppButton>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-3" style={{ borderRadius: 10 }}>
          Não foi possível carregar o cardápio: {error}
        </Alert>
      )}

      <Row className="mb-3 gy-2">
        <Col xs={12} md={5}>
          <SearchBar placeholder="Buscar produto..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </Col>
        <Col xs={6} md={3}>
          <Form.Select
            size="sm"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            aria-label="Filtrar por categoria"
          >
            <option value="">Todas as categorias</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{titleCase(c.name)}</option>
            ))}
          </Form.Select>
        </Col>
        <Col xs={6} md={3}>
          <Form.Select
            size="sm"
            value={availabilityFilter}
            onChange={(e) => setAvailabilityFilter(e.target.value)}
            aria-label="Filtrar por disponibilidade"
          >
            {AVAILABILITY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {isFiltering && (
        <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
          {search.trim() && (
            <button type="button" className="menu-filter-chip" onClick={() => setSearch('')}>
              "{search.trim()}" <span aria-hidden="true">×</span>
            </button>
          )}
          {categoryFilter && (
            <button type="button" className="menu-filter-chip" onClick={() => setCategoryFilter('')}>
              {titleCase(categories.find((c) => c.id === categoryFilter)?.name)} <span aria-hidden="true">×</span>
            </button>
          )}
          {availabilityFilter !== 'all' && (
            <button type="button" className="menu-filter-chip" onClick={() => setAvailabilityFilter('all')}>
              {AVAILABILITY_OPTIONS.find((o) => o.value === availabilityFilter)?.label} <span aria-hidden="true">×</span>
            </button>
          )}
          <span style={{ fontSize: 12, color: '#aaa' }}>
            {totalResults} {totalResults === 1 ? 'resultado' : 'resultados'} · arraste desabilitado durante o filtro
          </span>
        </div>
      )}

      {categories.length === 0 && !error && (
        <div className="text-center py-5 text-muted">
          <p className="mb-3">Nenhuma categoria cadastrada ainda.</p>
          <AppButton variant="primary" onClick={() => setCategoryModal('new')} className="d-flex align-items-center gap-1 mx-auto">
            <IconTag size={15} /> Criar a primeira categoria
          </AppButton>
        </div>
      )}

      {isFiltering && totalResults === 0 && categories.length > 0 ? (
        <div className="text-center py-5 text-muted">
          <p className="mb-3">Nenhum produto encontrado com esses filtros.</p>
          <AppButton variant="ghost" onClick={clearFilters}>Limpar filtros</AppButton>
        </div>
      ) : (
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="categories" type="category" isDropDisabled={isFiltering}>
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {grouped.map(({ category, products: categoryProducts }, index) => (
                <Draggable
                  key={category.id}
                  draggableId={`category-${category.id}`}
                  index={index}
                  isDragDisabled={isFiltering}
                >
                  {(dragProvided, dragSnapshot) => (
                    <div
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      className={`menu-category${dragSnapshot.isDragging ? ' dragging' : ''}`}
                      style={dragProvided.draggableProps.style}
                    >
                      <div className="menu-category-header d-flex justify-content-between align-items-center gap-2">
                        <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                          {!isFiltering && (
                            <span
                              {...dragProvided.dragHandleProps}
                              className="drag-handle menu-category-drag-handle"
                              title="Arraste para reordenar a categoria"
                              aria-label="Arraste para reordenar a categoria"
                            >
                              <IconGrip size={16} />
                            </span>
                          )}
                          {category.icon && <span style={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>{category.icon}</span>}
                          <span className="menu-category-name">{titleCase(category.name)}</span>
                          {/* Roxo sólido em vez do "ghost" padrão do AppBadge: aqui o fundo
                              do próprio cabeçalho já é o ghost, e o badge sumia nele por
                              cima da mesma cor. */}
                          <AppBadge
                            style={{ flexShrink: 0, backgroundColor: 'var(--color-purple-primary)', color: '#fff', border: 'none' }}
                          >
                            {categoryProducts.length}
                          </AppBadge>
                          {category.allowsHalfAndHalf && (
                            <span
                              className="half-and-half-badge d-none d-sm-inline-flex"
                              title="O cliente pode montar um item com dois sabores desta categoria"
                            >
                              ½
                            </span>
                          )}
                          {/* Os adicionais da categoria só apareciam dentro do modal de
                              edição — quem olhava só a lista do cardápio não tinha como
                              saber que a categoria já tinha adicionais cadastrados. */}
                          {category.addons?.length > 0 && (
                            <button
                              type="button"
                              className="menu-category-addons-badge d-none d-sm-inline-flex"
                              onClick={() => setCategoryModal(category)}
                              title="Ver adicionais desta categoria"
                            >
                              <IconPlusCircle size={13} />
                              {category.addons.length} {category.addons.length > 1 ? 'adicionais' : 'adicional'}
                            </button>
                          )}
                          {!category.isActive && <AppBadge variant="amber" className="d-none d-sm-inline-flex">Inativa</AppBadge>}
                        </div>
                        <div className="d-flex align-items-center gap-1 flex-shrink-0">
                          <button
                            type="button"
                            className="action-btn action-btn-filled d-none d-sm-inline-flex"
                            onClick={() => handleNewProduct(category.id)}
                          >
                            + Produto
                          </button>
                          <button
                            type="button"
                            className="action-btn icon-btn action-btn-filled d-sm-none"
                            onClick={() => handleNewProduct(category.id)}
                            title="Novo produto nesta categoria"
                            aria-label="Novo produto nesta categoria"
                          >
                            <IconPlus size={15} />
                          </button>
                          <button
                            type="button"
                            className="action-btn icon-btn action-btn-muted"
                            onClick={() => setCategoryModal(category)}
                            title="Editar categoria"
                            aria-label="Editar categoria"
                          >
                            <IconPencil size={15} />
                          </button>
                          <Dropdown align="end">
                            <Dropdown.Toggle
                              as="button"
                              type="button"
                              bsPrefix="action-btn icon-btn action-btn-muted"
                              title="Mais ações"
                              aria-label="Mais ações"
                            >
                              <IconMoreVertical size={15} />
                            </Dropdown.Toggle>
                            <Dropdown.Menu style={{ minWidth: 190, borderRadius: 10, fontSize: 13 }}>
                              {!isFiltering && index > 0 && (
                                <Dropdown.Item
                                  onClick={() => moveCategory(category, -1)}
                                  className="d-flex align-items-center gap-2"
                                  style={{ borderRadius: 6, padding: '8px 10px' }}
                                >
                                  <IconChevronDown size={14} style={{ transform: 'rotate(180deg)' }} /> Mover para cima
                                </Dropdown.Item>
                              )}
                              {!isFiltering && index < grouped.length - 1 && (
                                <Dropdown.Item
                                  onClick={() => moveCategory(category, 1)}
                                  className="d-flex align-items-center gap-2"
                                  style={{ borderRadius: 6, padding: '8px 10px' }}
                                >
                                  <IconChevronDown size={14} /> Mover para baixo
                                </Dropdown.Item>
                              )}
                              <Dropdown.Item
                                onClick={() => handleDeleteCategory(category)}
                                className="d-flex align-items-center gap-2"
                                style={{ color: '#e74c3c', borderRadius: 6, padding: '8px 10px' }}
                              >
                                <IconTrash size={14} /> Excluir categoria
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      </div>

                      <div className="menu-category-body">
                        <MenuList
                          categoryId={category.id}
                          categoryIcon={category.icon}
                          products={categoryProducts}
                          onEdit={(p) => setProductModal(p)}
                          onDelete={handleDeleteProduct}
                          onToggleAvailability={handleToggleAvailability}
                          onMove={moveProduct}
                          dragDisabled={isFiltering}
                          emptyLabel={isFiltering ? 'Nenhum produto encontrado.' : 'Nenhum produto nesta categoria ainda. Arraste um para cá ou crie um novo.'}
                        />
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      )}

      {categoryModal && (
        <CategoryFormModal
          show={!!categoryModal}
          category={categoryModal === 'new' ? null : categoryModal}
          nextSortOrder={nextCategorySortOrder}
          onHide={() => setCategoryModal(null)}
          onSaved={load}
        />
      )}

      {productModal && (
        <ProductFormModal
          show={!!productModal}
          product={productModal.isNew ? null : productModal}
          defaultCategoryId={productModal.categoryId}
          categories={categories}
          nextSortOrder={nextProductSortOrder}
          onHide={() => setProductModal(null)}
          onSaved={load}
        />
      )}

      {dialog}
    </AdminLayout>
  );
}

export default MenuPage;
